import { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Factory, Loader2, Plus, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataTable } from '@/components/ui/DataTable';
import { NewSupplierDialog } from '@/components/catalog/NewSupplierDialog';
import {
  usePushVendorToQbo,
  useVendors,
  VENDOR_TYPE_LABELS,
  type Vendor,
} from '@/features/procurement/hooks';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { formatDate, formatPhone } from '@/lib/formatters';

/**
 * Supplier directory — every vendor Atlas buys raw materials,
 * services, or finished goods from. Rows come from `public.vendors`
 * (which also receives vendors synced from QuickBooks); the "Add
 * supplier" dialog lets an admin enter one manually, and clicking a
 * row opens the same dialog in edit mode.
 *
 * QuickBooks is treated as a write-back mirror: edits land in Atlas
 * first (badge flips to "Pending push") and the per-row "Push" button
 * sends them to QBO via the `qbo-vendor-push` edge function — creating
 * the QBO Vendor when the row was born in Atlas.
 */
export function SuppliersPage() {
  const { role } = useAuth();
  const { data, isLoading } = useVendors();
  const pushVendor = usePushVendorToQbo();
  const [newOpen, setNewOpen] = useState(false);
  const [editVendor, setEditVendor] = useState<Vendor | null>(null);

  // Mirrors the `vendors_write_admin` RLS policy (admin / super_admin
  // only) so other roles never bounce off a "permission denied".
  const canCreate = canWrite(role, 'procurement');

  const rows = useMemo<Vendor[]>(() => data ?? [], [data]);

  const pushingId = pushVendor.isPending ? pushVendor.variables : null;

  const handlePush = (vendor: Vendor) => {
    pushVendor.mutate(vendor.id, {
      onSuccess: () => toast.success(`Pushed “${vendor.name}” to QuickBooks.`),
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Push to QuickBooks failed.'),
    });
  };

  const qbStatusBadge = (vendor: Vendor) => {
    if (!vendor.qb_external_id) {
      return <Badge variant="outline">Not in QB</Badge>;
    }
    return vendor.qb_sync_status === 'synced' ? (
      <Badge variant="info">Synced</Badge>
    ) : (
      <Badge variant="warning">Pending push</Badge>
    );
  };

  const columns = useMemo<ColumnDef<Vendor>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        size: 220,
        minSize: 120,
        cell: ({ row }) => (
          <span className="font-medium text-foreground">{row.original.name}</span>
        ),
      },
      {
        id: 'vendor_type',
        accessorKey: 'vendor_type',
        header: 'Type',
        size: 160,
        minSize: 110,
        cell: ({ row }) =>
          row.original.vendor_type ? (
            <Badge variant="secondary">{VENDOR_TYPE_LABELS[row.original.vendor_type]}</Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'supplies',
        accessorKey: 'supplies',
        header: 'Supplies',
        size: 200,
        minSize: 120,
        cell: ({ row }) =>
          row.original.supplies ? (
            <span className="line-clamp-2 text-muted-foreground" title={row.original.supplies}>
              {row.original.supplies}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'contact_name',
        accessorKey: 'contact_name',
        header: 'Contact',
        size: 150,
        minSize: 100,
        cell: ({ row }) =>
          row.original.contact_name ?? <span className="text-muted-foreground">—</span>,
      },
      {
        id: 'email',
        accessorKey: 'email',
        header: 'Email',
        size: 200,
        minSize: 120,
        cell: ({ row }) =>
          row.original.email ? (
            <a
              href={`mailto:${row.original.email}`}
              onClick={(e) => e.stopPropagation()}
              className="text-foreground hover:underline"
            >
              {row.original.email}
            </a>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'phone',
        accessorFn: (row) => formatPhone(row.phone),
        header: 'Phone',
        size: 130,
        minSize: 100,
        cell: ({ row }) =>
          formatPhone(row.original.phone) || <span className="text-muted-foreground">—</span>,
      },
      {
        id: 'is_active',
        accessorKey: 'is_active',
        header: 'Status',
        size: 100,
        minSize: 90,
        cell: ({ row }) =>
          row.original.is_active ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge variant="muted">Inactive</Badge>
          ),
      },
      {
        id: 'qb_status',
        accessorFn: (row) =>
          !row.qb_external_id ? 'not_in_qb' : row.qb_sync_status,
        header: 'QB Status',
        size: 130,
        minSize: 110,
        cell: ({ row }) => qbStatusBadge(row.original),
      },
      {
        id: 'created_at',
        accessorKey: 'created_at',
        header: 'Added',
        size: 110,
        minSize: 100,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
      ...(canCreate
        ? ([
            {
              id: 'push',
              header: '',
              size: 110,
              minSize: 100,
              enableSorting: false,
              cell: ({ row }) => {
                const vendor = row.original;
                const needsPush =
                  !vendor.qb_external_id || vendor.qb_sync_status === 'pending';
                if (!needsPush) return null;
                const isPushing = pushingId === vendor.id;
                return (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={pushVendor.isPending}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePush(vendor);
                    }}
                  >
                    {isPushing ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <UploadCloud className="h-3.5 w-3.5" />
                    )}
                    {isPushing ? 'Pushing…' : 'Push'}
                  </Button>
                );
              },
            },
          ] as ColumnDef<Vendor>[])
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canCreate, pushingId, pushVendor.isPending],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Suppliers"
        actions={
          canCreate ? (
            <Button onClick={() => setNewOpen(true)}>
              <Plus className="h-4 w-4" />
              Add supplier
            </Button>
          ) : undefined
        }
      />

      <Card>
        <CardContent className="pt-6">
          <DataTable<Vendor, unknown>
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(r) => r.id}
            onRowClick={canCreate ? (row) => setEditVendor(row) : undefined}
            searchPlaceholder="Search by name, email, or website…"
            initialPageSize={25}
            pageSizes={[10, 25, 50, 100]}
            tableId="catalog-suppliers"
            mobileCard={{
              title: (row) => row.name,
              subtitle: (row) => row.email ?? row.website ?? undefined,
              badges: (row) => [
                row.vendor_type ? (
                  <Badge key="type" variant="secondary">
                    {VENDOR_TYPE_LABELS[row.vendor_type]}
                  </Badge>
                ) : null,
                row.is_active ? (
                  <Badge key="status" variant="success">
                    Active
                  </Badge>
                ) : (
                  <Badge key="status" variant="muted">
                    Inactive
                  </Badge>
                ),
                <span key="qb" className="contents">
                  {qbStatusBadge(row)}
                </span>,
              ],
              meta: (row) => [
                ...(row.phone ? [{ label: 'Phone', value: formatPhone(row.phone) }] : []),
                ...(row.contact_name ? [{ label: 'Contact', value: row.contact_name }] : []),
              ],
            }}
            emptyState={
              <EmptyState
                icon={Factory}
                title="No suppliers yet"
                action={
                  canCreate ? (
                    <Button onClick={() => setNewOpen(true)}>
                      <Plus className="h-4 w-4" />
                      Add supplier
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </CardContent>
      </Card>

      <NewSupplierDialog open={newOpen} onOpenChange={setNewOpen} />
      <NewSupplierDialog
        open={editVendor !== null}
        onOpenChange={(open) => {
          if (!open) setEditVendor(null);
        }}
        vendor={editVendor}
      />
    </div>
  );
}
